import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import Login from './Login';
import Alert from './Alert';
import { setCaseDbCsrfToken } from '../utils/csrf';
import '../Context.css'; // Import Context styles for Microsoft login button

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const [formData, setFormData] = useState({ user: '', password: '' });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msLoginLoading, setMsLoginLoading] = useState(false);
  const popupIntervalRef = useRef(null);

  useEffect(() => {
    const handleMessage = (event) => {
      console.log('Received message:', event.origin, event.data);
      
      // Ensure the message is from the expected origin (allow localhost for development)
      const allowedOrigins = ['https://api.casedb.co', 'http://localhost:3000'];
      if (!allowedOrigins.includes(event.origin) && event.origin !== window.location.origin) {
        console.log('Message rejected - wrong origin:', event.origin);
        return;
      }

      if (event.data.type === 'MICROSOFT_LOGIN_SUCCESS') {
        console.log('Microsoft login success received');
        setMsLoginLoading(false);
        setError(null); // Clear any previous errors
        // Clear the popup monitoring interval
        if (popupIntervalRef.current) {
          clearInterval(popupIntervalRef.current);
          popupIntervalRef.current = null;
        }
        onLoginSuccess(event.data.userData);
        // The modal will be closed by the parent component's handleLoginModalSuccess
      } else if (event.data.type === 'MICROSOFT_LOGIN_ERROR') {
        console.log('Microsoft login error received:', event.data.error);
        setMsLoginLoading(false);
        setError(event.data.error || 'Microsoft login failed');
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onLoginSuccess]);

  const handleMicrosoftLogin = () => {
    setMsLoginLoading(true);
    setError(null);
    
    const tenantId = "5a29d260-1076-4d36-b1a8-59564b8f3067";
    const clientId = "2c0e226b-2b15-4716-b743-ff7d4627c06a";
    const redirectUri = encodeURIComponent("https://api.casedb.co/graph_callback_popup.php");
    const scope = encodeURIComponent("User.Read Calendars.ReadWrite Mail.Send offline_access");
    
    const state = encodeURIComponent(JSON.stringify({
      random: Math.random().toString(36).substring(2, 15),
      isModal: true,
      timestamp: Date.now()
    }));

    const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?client_id=${clientId}&response_type=code&redirect_uri=${redirectUri}&response_mode=query&scope=${scope}&state=${state}`;
    
    // Open popup window
    const popup = window.open(
      authUrl,
      'microsoftLogin',
      'width=500,height=600,scrollbars=yes,resizable=yes,centerscreen=yes'
    );

    // Check if popup was blocked
    if (!popup) {
      setMsLoginLoading(false);
      setError('Popup was blocked. Please allow popups and try again.');
      return;
    }

    // Monitor popup for closure (in case user closes it manually)
    popupIntervalRef.current = setInterval(() => {
      if (popup.closed) {
        setMsLoginLoading(false);
        if (popupIntervalRef.current) {
          clearInterval(popupIntervalRef.current);
          popupIntervalRef.current = null;
        }
        // Don't set an error here - user may have just cancelled
      }
    }, 1000);
  };

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (popupIntervalRef.current) {
        clearInterval(popupIntervalRef.current);
      }
    };
  }, []);

  const handleFormLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('https://api.casedb.co/login.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        setCaseDbCsrfToken(data.csrf_token);
        localStorage.setItem('token', data.token);
        onLoginSuccess(data);
        onClose();
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      console.error('Error during login:', err);
      setError('An error occurred during login');
    } finally {
      setLoading(false);
    }
  };

  const formUpdate = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  if (!isOpen) return null;

  return (
    <Modal
        onClose={() => {}} 
        title="Please Sign In"
        header={<></>} 
        instructions={
        "Your session has expired. Please sign in again to continue."}
    >
      <div className="modal-content-wrapper" style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: '10px',
        padding: '20px' 
      }}>
        {error && <Alert message={error} type="error" onClose={() => setError(null)} />}
        

        {/* Primary Login - Microsoft */}
        <button
          type="button"
          className="microsoft-login"
          onClick={handleMicrosoftLogin}
          disabled={msLoginLoading}
          style={{ opacity: msLoginLoading ? 0.7 : 1 }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" height={16} width={16}>
            <path fill="#f35325" d="M0 0h10v10H0z"/>
            <path fill="#81bc06" d="M11 0h10v10H11z"/>
            <path fill="#05a6f0" d="M0 11h10v10H0z"/>
            <path fill="#ffba08" d="M11 11h10v10H11z"/>
          </svg>
          {msLoginLoading ? 'Opening Sign In...' : 'Sign In with Microsoft'}
        </button>

        {/* Fallback Option Divider */}
        <span className='or'>
          <div className='divider horizontal'></div>
          Use Legacy Login
          <div className='divider horizontal'></div>
        </span>

        {/* Fallback Login - Traditional */}
        <form onSubmit={handleFormLogin} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <Login formUpdate={formUpdate} formData={formData} />
          <button 
            type="submit" 
            disabled={loading}
            className={`action ${formData.password && formData.user ? '' : 'quaternary'}`}
          >
            {loading ? 'Signing In...' : 'Login with Password'}
          </button>
        </form>
      </div>
    </Modal>
  );
};

export default LoginModal;
